class ErrorHandler {
  constructor() {
    this.errorListeners = []
    this.isolationEnabled = true
  }

  init() {
    window.addEventListener('error', (event) => {
      this.handleGlobalError(event.error, {
        type: 'global',
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno
      })

      if (this.isolationEnabled) {
        event.preventDefault()
      }
    })

    window.addEventListener('unhandledrejection', (event) => {
      this.handleGlobalError(event.reason, {
        type: 'promise',
        promise: event.promise
      })

      if (this.isolationEnabled) {
        event.preventDefault()
      }
    })

    console.log('[ErrorHandler] Global error handlers initialized')
  }

  handleGlobalError(error, context = {}) {
    // Ignore errors from browser extensions
    const stack = error?.stack || ''
    const filename = context.filename || ''
    const extensionPatterns = ['chrome-extension://', 'moz-extension://', 'safari-extension://']
    const isExtensionError = extensionPatterns.some(
      (pattern) => stack.includes(pattern) || filename.includes(pattern)
    )

    if (isExtensionError) {
      console.debug('[ErrorHandler] Ignored browser extension error:', error?.message)
      return
    }

    const errorInfo = {
      message: error?.message || String(error),
      stack: error?.stack,
      context,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent
    }

    console.error('🚨 [ErrorHandler] Caught error:', errorInfo)
    this.notifyListeners(errorInfo)
  }

  createVueErrorHandler() {
    return (err, instance, info) => {
      this.handleGlobalError(err, {
        type: 'vue',
        componentName: instance?.$options?.name || 'Anonymous',
        info,
        props: instance?.$props
      })
    }
  }

  setupVue3ErrorHandler(app) {
    if (!app || typeof app.config !== 'object') {
      console.warn('[ErrorHandler] Invalid Vue app instance')
      return
    }

    app.config.errorHandler = this.createVueErrorHandler()

    if (import.meta.env.DEV) {
      app.config.warnHandler = (msg, instance, trace) => {
        console.warn('[Vue warn]:', msg, trace)
      }
    }

    // Integrate with monitoring system
    if (typeof window.__MONITOR__?.setupVue3ErrorHandler === 'function') {
      try {
        window.__MONITOR__.setupVue3ErrorHandler(app)
      } catch (error) {
        console.error('[ErrorHandler] Failed to integrate with __MONITOR__:', error)
      }
    }
  }

  safeAsync(fn, context = 'anonymous') {
    return async (...args) => {
      try {
        return await fn(...args)
      } catch (error) {
        this.handleGlobalError(error, {
          type: 'async-wrapper',
          context,
          args: args.length > 0 ? 'provided' : 'none'
        })
        return null
      }
    }
  }

  safeSync(fn, context = 'anonymous', fallback = null) {
    return (...args) => {
      try {
        return fn(...args)
      } catch (error) {
        this.handleGlobalError(error, {
          type: 'sync-wrapper',
          context,
          args: args.length > 0 ? 'provided' : 'none'
        })
        return fallback
      }
    }
  }

  safeModuleLoader(loader, moduleName) {
    return async () => {
      try {
        return await loader()
      } catch (error) {
        this.handleGlobalError(error, {
          type: 'module-load',
          moduleName
        })

        return {
          default: () => {
            console.warn(`[ErrorHandler] Module "${moduleName}" failed to load, using fallback`)
          }
        }
      }
    }
  }

  addListener(listener) {
    if (typeof listener === 'function') {
      this.errorListeners.push(listener)
    }
  }

  removeListener(listener) {
    const index = this.errorListeners.indexOf(listener)
    if (index > -1) {
      this.errorListeners.splice(index, 1)
    }
  }

  notifyListeners(errorInfo) {
    this.errorListeners.forEach((listener) => {
      try {
        listener(errorInfo)
      } catch (error) {
        console.error('[ErrorHandler] Listener error:', error)
      }
    })
  }

  setIsolation(enabled) {
    this.isolationEnabled = enabled
    console.log(`[ErrorHandler] Error isolation ${enabled ? 'enabled' : 'disabled'}`)
  }
}

const errorHandler = new ErrorHandler()

export default errorHandler
export { ErrorHandler }
